import { useMutation, useQuery } from "@apollo/client";
import AddTask from "@mui/icons-material/AddTask";
import Archive from "@mui/icons-material/Archive";
import ArchiveOutlined from "@mui/icons-material/ArchiveOutlined";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Check from "@mui/icons-material/Check";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnread from "@mui/icons-material/MarkEmailUnread";
import NotificationsActive from "@mui/icons-material/NotificationsActive";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import {
  GridActionsCellItem,
  type GridRowParams,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import { CreateLinkedTaskDialog } from "components/Notifications/CreateLinkedTaskDialog";
import { NotificationDownloadButton } from "components/Notifications/NotificationDownloadButton";
import {
  hasRenderableLiveStatus,
  NotificationLiveStatus,
} from "components/Notifications/NotificationLiveStatus";
import { NotificationMarkdown } from "components/Notifications/NotificationMarkdown";
import { getNotificationDestination } from "components/Notifications/notificationNavigation";
import {
  getNotificationCategoryLabel,
  getNotificationProgramAreaLabel,
} from "components/Notifications/notificationPresentation";
import { NotificationsTiles } from "components/Notifications/NotificationsTiles";
import { NotificationTypeIcon } from "components/Notifications/NotificationTypeIcon";
import { MyNotificationsQuery, NotificationView, ProgramArea } from "generated-graphql/graphql";
import { GridSelectionProvider, OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import {
  ARCHIVE_NOTIFICATION,
  GET_NOTIFICATION_USER,
  GET_MY_NOTIFICATIONS,
  GET_MY_NOTIFICATION_VIEWS,
  GET_MY_UNREAD_NOTIFICATION_COUNT,
  MARK_NOTIFICATION_READ,
  MARK_NOTIFICATION_UNREAD,
} from "hooks/useNotifications";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { NotificationBulkActions } from "./NotificationBulkActions";

type NotificationRow = MyNotificationsQuery["myNotifications"]["items"][number];

/**
 * The subject type whose notifications can be turned into a task. Compared as a
 * string because the API deliberately keeps `subject.type` an open registry key
 * rather than a GraphQL enum.
 */
const TASK_SUBJECT_TYPE = "TASK";

function toUtcDateTime(value: unknown) {
  if (!value) {
    return DateTime.invalid("Missing date");
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value, { zone: "utc" });
  }
  if (typeof value === "string") {
    return DateTime.fromISO(value, { zone: "utc" });
  }
  return DateTime.invalid("Unsupported date");
}

function formatDate(value: unknown) {
  const dateTime = toUtcDateTime(value);
  if (!dateTime.isValid) {
    return "Not recorded";
  }
  return dateTime.toLocaleString(DateTime.DATETIME_MED);
}

const NOTIFICATION_INITIAL_SORT_MODEL = [{ field: "createdAt", sort: "desc" as const }];

const NOTIFICATION_COMMAND_BUTTONS = [<NotificationBulkActions key="notification-bulk-actions" />];

const NOTIFICATION_GRID_SX = {
  "& .notification-row--unread": {
    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.035),
  },
  "& .notification-row--unread:hover": {
    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.07),
  },
  "& .notification-row--unread .MuiDataGrid-cell": {
    color: "text.primary",
    fontWeight: 700,
  },
  "& .notification-row--unread .MuiTypography-root": {
    color: "text.primary",
    fontWeight: 700,
  },
};

function getNotificationItems(data: MyNotificationsQuery) {
  return data.myNotifications.items;
}

function getNotificationCount(data: MyNotificationsQuery) {
  return data.myNotifications.count;
}

/**
 * What a row's status column reads as: the subject's live status when the
 * server resolved one, otherwise "Resolved" for a notification the platform has
 * closed out. Resolved notifications stay in the list — annotated, never hidden
 * and never auto-read.
 */
function getNotificationStatusText(notification: NotificationRow["notification"]) {
  const liveStatus = notification.subject?.liveStatus;
  if (hasRenderableLiveStatus(liveStatus)) {
    return liveStatus?.status ?? "";
  }
  return notification.resolvedAt ? "Resolved" : "";
}

function NotificationSubjectStatus({
  notification,
}: {
  notification: NotificationRow["notification"];
}) {
  const liveStatus = notification.subject?.liveStatus;
  if (hasRenderableLiveStatus(liveStatus)) {
    return <NotificationLiveStatus liveStatus={liveStatus} />;
  }

  if (notification.resolvedAt) {
    return <Chip size="small" label="Resolved" color="success" variant="outlined" />;
  }

  return null;
}

function removeLegacyNotificationSearchParam(search: string) {
  const searchParams = new URLSearchParams(search);
  searchParams.delete("notificationUserId");
  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : "";
}

export function NotificationDetail({ notificationUserId }: { notificationUserId: string }) {
  const { tenantId } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const listSearch = removeLegacyNotificationSearchParam(location.search);
  const listUrl = `/notifications${listSearch}`;
  const autoReadIdsRef = useRef<Set<string>>(new Set());
  const userToggledReadRef = useRef<Set<string>>(new Set());
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const refetchQueries = [GET_MY_NOTIFICATIONS, GET_MY_UNREAD_NOTIFICATION_COUNT];
  const {
    data: notificationData,
    loading: notificationLoading,
    refetch: refetchNotification,
  } = useQuery(GET_NOTIFICATION_USER, {
    variables: { id: notificationUserId },
    fetchPolicy: "cache-and-network",
  });
  const [markRead, markReadState] = useMutation(MARK_NOTIFICATION_READ, {
    refetchQueries,
  });
  const [markUnread, markUnreadState] = useMutation(MARK_NOTIFICATION_UNREAD, {
    refetchQueries,
  });
  const [archiveNotification, archiveState] = useMutation(ARCHIVE_NOTIFICATION, { refetchQueries });
  const notificationUser = notificationData?.notificationUser;
  const notification = notificationUser?.notification;
  const isMutating = markReadState.loading || markUnreadState.loading || archiveState.loading;

  useBreadcrumb([
    { label: "Notifications", to: listUrl },
    { label: notification?.title ?? "Notification" },
  ]);

  useEffect(() => {
    if (
      !notificationUser ||
      notificationUser.readAt ||
      autoReadIdsRef.current.has(notificationUser.id) ||
      userToggledReadRef.current.has(notificationUser.id)
    ) {
      return;
    }

    autoReadIdsRef.current.add(notificationUser.id);
    void markRead({
      variables: { notificationUserId: notificationUser.id },
      optimisticResponse: {
        markNotificationRead: {
          __typename: "NotificationUser" as const,
          id: notificationUser.id,
          readAt: new Date().toISOString(),
        },
      },
    }).catch(() => {});
  }, [markRead, notificationUser]);

  const navigateToList = () => {
    navigate(listUrl);
  };

  const handleToggleRead = async () => {
    if (!notificationUser) {
      return;
    }

    userToggledReadRef.current.add(notificationUser.id);
    const nowIso = new Date().toISOString();
    if (notificationUser.readAt) {
      await markUnread({
        variables: { notificationUserId: notificationUser.id },
        optimisticResponse: {
          markNotificationUnread: {
            __typename: "NotificationUser" as const,
            id: notificationUser.id,
            readAt: null,
          },
        },
      });
    } else {
      await markRead({
        variables: { notificationUserId: notificationUser.id },
        optimisticResponse: {
          markNotificationRead: {
            __typename: "NotificationUser" as const,
            id: notificationUser.id,
            readAt: nowIso,
          },
        },
      });
    }
  };

  const handleArchive = async () => {
    if (!notificationUser) {
      return;
    }

    const nowIso = new Date().toISOString();
    await archiveNotification({
      variables: { notificationUserId: notificationUser.id },
      optimisticResponse: {
        archiveNotification: {
          __typename: "NotificationUser" as const,
          id: notificationUser.id,
          readAt: notificationUser.readAt ?? nowIso,
          archivedAt: nowIso,
        },
      },
    });
    navigateToList();
  };

  if (notificationLoading && !notificationUser) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!notificationUser || !notification) {
    return (
      <Stack spacing={2} sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Button startIcon={<ArrowBack />} onClick={navigateToList} sx={{ alignSelf: "flex-start" }}>
          Notifications
        </Button>
        <Paper variant="outlined" sx={{ borderRadius: 1, p: 3 }}>
          <Typography variant="h6">Notification not found</Typography>
          <Typography variant="body2" color="text.secondary">
            The notification may have been archived or is no longer available to your user.
          </Typography>
        </Paper>
      </Stack>
    );
  }

  let readToggleLabel = "Mark as read";
  if (notificationUser.archivedAt) {
    readToggleLabel = notificationUser.readAt ? "Read" : "Unread";
  } else if (notificationUser.readAt) {
    readToggleLabel = "Mark as unread";
  }

  const linkedTenantId = notification.tenant?.id ?? tenantId;
  const isResolved = Boolean(notification.resolvedAt);
  const isArchived = Boolean(notificationUser.archivedAt);
  const isUnread = !notificationUser.readAt;
  // This page is a signpost, not a destination: the record the notification is
  // about owns the detail, and the same rule the bell and the inbox use decides
  // where that is. A subject that no longer resolves keeps its fallback label
  // but loses the link rather than offering a dead one.
  const subject = notification.subject ?? null;
  const subjectDestination = subject?.route
    ? getNotificationDestination({
        id: notificationUser.id,
        readAt: notificationUser.readAt,
        notification,
      })
    : null;
  const isTaskSubject = subject?.type === TASK_SUBJECT_TYPE;
  const canCreateTask = !isTaskSubject && !isArchived && Boolean(linkedTenantId);

  const handleLinkedTaskCreated = async () => {
    setCreateTaskOpen(false);
    await refetchNotification();
  };

  return (
    <Stack py={3} spacing={2} sx={{ width: "100%", px: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Button startIcon={<ArrowBack />} onClick={navigateToList} sx={{ alignSelf: "flex-start" }}>
          Notifications
        </Button>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {subject && subjectDestination ? (
            <Button
              component={RouterLink}
              to={subjectDestination}
              variant="outlined"
              endIcon={<OpenInNew />}
            >
              {`Open ${subject.label}`}
            </Button>
          ) : null}
          {subject && !subjectDestination ? (
            <Typography variant="body2" color="text.secondary">
              {subject.label}
            </Typography>
          ) : null}
          {canCreateTask ? (
            <Button
              variant="outlined"
              startIcon={<AddTask />}
              onClick={() => setCreateTaskOpen(true)}
            >
              Create task
            </Button>
          ) : null}
          <Button
            variant="outlined"
            startIcon={notificationUser.readAt ? <MarkEmailUnread /> : <MarkEmailRead />}
            onClick={handleToggleRead}
            disabled={isMutating || isArchived}
          >
            {readToggleLabel}
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<Archive />}
            onClick={handleArchive}
            disabled={isMutating || isArchived}
          >
            {isArchived ? "Archived" : "Archive"}
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
        <Stack spacing={0}>
          <Box
            sx={{
              bgcolor: "background.default",
              px: { xs: 2, md: 3 },
              py: 2.5,
            }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    flexShrink: 0,
                    "& .MuiSvgIcon-root, & svg": { fontSize: 36 },
                  }}
                >
                  <NotificationTypeIcon
                    category={notification.category}
                    programArea={notification.programArea ?? null}
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    overflowWrap: "anywhere",
                    lineHeight: 1.2,
                    minWidth: 0,
                  }}
                >
                  {notification.title}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ pl: { xs: 0, sm: 6.5 } }}
              >
                <Chip
                  size="small"
                  label={getNotificationCategoryLabel(notification.category)}
                  variant="outlined"
                />
                {notification.programArea ? (
                  <Chip
                    size="small"
                    label={getNotificationProgramAreaLabel(notification.programArea)}
                    variant="outlined"
                  />
                ) : null}
                <Chip
                  size="small"
                  label={isUnread ? "Unread" : "Read"}
                  color={isUnread ? "primary" : "default"}
                  variant={isUnread ? "filled" : "outlined"}
                />
                {isResolved ? (
                  <Chip size="small" label="Resolved" color="success" variant="outlined" />
                ) : null}
                {isArchived ? <Chip size="small" label="Archived" variant="outlined" /> : null}
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(notification.createdAt)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <Divider />
          <Box
            sx={{
              maxWidth: 820,
              px: { xs: 2, md: 3 },
              py: 3,
              "& .MuiTypography-root": { fontSize: "1rem" },
              "& .MuiTypography-root:last-child": { mb: 0 },
            }}
          >
            <NotificationMarkdown bodyMarkdown={notification.bodyMarkdown} />
            <NotificationDownloadButton subject={subject} />
          </Box>
        </Stack>
      </Paper>

      {createTaskOpen && linkedTenantId ? (
        <CreateLinkedTaskDialog
          open={createTaskOpen}
          onClose={() => setCreateTaskOpen(false)}
          onCreated={handleLinkedTaskCreated}
          notificationId={notification.id}
          tenantId={linkedTenantId}
          defaultTitle={notification.title}
          bodyMarkdown={notification.bodyMarkdown ?? ""}
          defaultFacilityId={notification.facility?.id ?? null}
        />
      ) : null}
    </Stack>
  );
}

function isNotificationView(value: string): value is NotificationView {
  return Object.values(NotificationView).includes(value as NotificationView);
}

function NotificationsTable() {
  return (
    <GridSelectionProvider>
      <NotificationsTableContent />
    </GridSelectionProvider>
  );
}

function NotificationsTableContent() {
  useBreadcrumb({ label: "Notifications" });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const activeView: NotificationView | null =
    viewParam && isNotificationView(viewParam) ? viewParam : null;

  const refetchQueries = [
    GET_MY_NOTIFICATIONS,
    GET_MY_UNREAD_NOTIFICATION_COUNT,
    GET_MY_NOTIFICATION_VIEWS,
  ];
  const [markRead] = useMutation(MARK_NOTIFICATION_READ, { refetchQueries });
  const [markUnread] = useMutation(MARK_NOTIFICATION_UNREAD, {
    refetchQueries,
  });
  const [archiveNotification] = useMutation(ARCHIVE_NOTIFICATION, {
    refetchQueries,
  });

  // Opening a row goes straight to the record the notification is about and
  // marks the receipt read in the same action — the same rule the bell uses, so
  // the two surfaces cannot drift.
  const handleRowClick = useCallback(
    (params: GridRowParams<NotificationRow>) => {
      const row = params.row;
      if (!row.readAt) {
        void markRead({
          variables: { notificationUserId: row.id },
          optimisticResponse: {
            markNotificationRead: {
              __typename: "NotificationUser" as const,
              id: row.id,
              readAt: new Date().toISOString(),
            },
          },
        }).catch(() => {});
      }
      navigate(getNotificationDestination(row));
    },
    [markRead, navigate]
  );

  const handleToggleRead = useCallback(
    (row: NotificationRow) => {
      const notificationUserId = row.id;
      if (row.readAt) {
        void markUnread({
          variables: { notificationUserId },
          optimisticResponse: {
            markNotificationUnread: {
              __typename: "NotificationUser" as const,
              id: notificationUserId,
              readAt: null,
            },
          },
        }).catch(() => {});
      } else {
        void markRead({
          variables: { notificationUserId },
          optimisticResponse: {
            markNotificationRead: {
              __typename: "NotificationUser" as const,
              id: notificationUserId,
              readAt: new Date().toISOString(),
            },
          },
        }).catch(() => {});
      }
    },
    [markRead, markUnread]
  );

  const handleArchive = useCallback(
    (row: NotificationRow) => {
      const notificationUserId = row.id;
      const nowIso = new Date().toISOString();
      void archiveNotification({
        variables: { notificationUserId },
        optimisticResponse: {
          archiveNotification: {
            __typename: "NotificationUser" as const,
            id: notificationUserId,
            readAt: row.readAt ?? nowIso,
            archivedAt: nowIso,
          },
        },
      }).catch(() => {});
    },
    [archiveNotification]
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch || undefined,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((entry) => ({ field: entry.field, sort: entry.sort })),
      includeArchived: false,
      view: activeView ?? undefined,
    }),
    [activeView]
  );

  const getNotificationRowClassName = useCallback(
    (params: GridRowParams<NotificationRow>) =>
      !params.row.readAt ? "notification-row--unread" : "",
    []
  );

  const columns = useMemo(
    (): OmnisearchGridColDef<NotificationRow>[] => [
      {
        field: "title",
        headerName: "Title",
        flex: 1,
        valueGetter(_, row) {
          return row.notification.title;
        },
        renderCell(params) {
          return (
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
              <NotificationTypeIcon
                category={params.row.notification.category}
                programArea={params.row.notification.programArea ?? null}
              />
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {params.row.notification.title}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "liveStatus",
        headerName: "Status",
        flex: 0.32,
        // Resolved at query time from the subject record rather than stored on
        // the notification, so there is no column for the server to sort or
        // filter on.
        sortable: false,
        filterable: false,
        valueGetter(_, row) {
          return getNotificationStatusText(row.notification);
        },
        renderCell(params) {
          return <NotificationSubjectStatus notification={params.row.notification} />;
        },
      },
      {
        field: "programArea",
        headerName: "Program",
        flex: 0.28,
        valueGetter(_, row) {
          return row.notification.programArea
            ? getNotificationProgramAreaLabel(row.notification.programArea)
            : "";
        },
        renderCell(params) {
          if (!params.row.notification.programArea) {
            return null;
          }

          return (
            <Typography variant="body2" color="text.secondary" noWrap>
              {getNotificationProgramAreaLabel(params.row.notification.programArea)}
            </Typography>
          );
        },
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ProgramArea),
        enumPresentationFunction: (value) => getNotificationProgramAreaLabel(value),
      },
      {
        field: "facility",
        headerName: "Facility",
        flex: 0.5,
        valueGetter(_, row) {
          return row.notification.facility?.name ?? "";
        },
      },
      {
        field: "createdAt",
        headerName: "Received",
        flex: 0.45,
        filterKeyType: "date",
        valueGetter(_, row) {
          return formatDate(row.createdAt);
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        flex: 0.12,
        minWidth: 48,
        getActions(params: GridRowParams<NotificationRow>) {
          const isRead = Boolean(params.row.readAt);
          return [
            <GridActionsCellItem
              key="toggle-read"
              icon={isRead ? <NotificationsActive fontSize="small" /> : <Check fontSize="small" />}
              label={isRead ? "Mark as unread" : "Mark as read"}
              onClick={() => handleToggleRead(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              key="archive"
              icon={<ArchiveOutlined fontSize="small" />}
              label="Archive"
              onClick={() => handleArchive(params.row)}
              showInMenu
            />,
          ];
        },
      },
    ],
    [handleArchive, handleToggleRead]
  );

  const handleSelectView = (next: NotificationView | null) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next) {
      // Tile selection is mutually exclusive with column-driven filters; clear
      // any URL-persisted filter/search state when activating a tile.
      Array.from(nextParams.keys()).forEach((key) => {
        if (key !== "view") nextParams.delete(key);
      });
      nextParams.set("view", next);
    } else {
      nextParams.delete("view");
    }
    setSearchParams(nextParams, { replace: false });
  };

  return (
    <Stack py={3} spacing={2}>
      <Box sx={{ px: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Your inbox across all organizations. Read state is personal — marking a notification read
          here does not change it for other recipients.
        </Typography>
        <NotificationsTiles activeView={activeView} onSelect={handleSelectView} />
      </Box>
      <OmnisearchDataGridV2
        query={GET_MY_NOTIFICATIONS}
        persistenceKey="customerNotifications"
        checkboxSelection
        disableRowSelectionOnClick
        columns={columns}
        getItems={getNotificationItems}
        getCount={getNotificationCount}
        buildVariables={buildVariables}
        commandButtons={NOTIFICATION_COMMAND_BUTTONS}
        onRowClick={handleRowClick}
        getRowClassName={getNotificationRowClassName}
        gridSx={NOTIFICATION_GRID_SX}
        initialSortModel={NOTIFICATION_INITIAL_SORT_MODEL}
        noRowsMessage="No notifications"
        sx={{ pt: 0 }}
      />
    </Stack>
  );
}

export function Notifications() {
  const { notificationUserId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const legacyNotificationUserId = searchParams.get("notificationUserId");

  useEffect(() => {
    if (notificationUserId || !legacyNotificationUserId) {
      return;
    }

    const nextSearch = removeLegacyNotificationSearchParam(`?${searchParams.toString()}`);
    navigate(`${encodeURIComponent(legacyNotificationUserId)}${nextSearch}`, {
      replace: true,
    });
  }, [legacyNotificationUserId, navigate, notificationUserId, searchParams]);

  if (notificationUserId) {
    return <NotificationDetail notificationUserId={notificationUserId} />;
  }

  return <NotificationsTable />;
}
